import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { supabase } from '@/integrations/supabase/client';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useAuth } from '@/providers/AuthProvider';
import InstallationKeyModal from '@/components/account/InstallationKeyModal';
import InstallationVoiceConfigModal from './InstallationVoiceConfigModal';
import { Settings2 } from 'lucide-react';

const formSchema = z.object({
  domain: z.string()
    .min(4, "Domain must be at least 4 characters")
    .max(253, "Domain is too long")
    .regex(/^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z0-9][a-z0-9-]{0,61}[a-z0-9]$/i, 
      "Please enter a valid domain (e.g., example.com)")
});

export function ManageInstallations() {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [selectedInstallation, setSelectedInstallation] = useState<{ key: string, domain: string } | null>(null);
  const [voiceConfigInstallation, setVoiceConfigInstallation] = useState<{ key: string, domain: string } | null>(null);
  
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      domain: "",
    },
  });

  const { data: installations, isLoading } = useQuery({
    queryKey: ['installations', user?.id],
    queryFn: async () => {
      if (!user?.id) throw new Error("User not authenticated");
      
      const { data, error } = await supabase
        .from('installations')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data;
    },
    enabled: !!user?.id,
  });

  const createInstallation = useMutation({
    mutationFn: async (domain: string) => {
      if (!user?.id) throw new Error("User not authenticated");
      
      const installKey = crypto.randomUUID();
      const { error } = await supabase
        .from('installations')
        .insert({
          domain: domain.toLowerCase(),
          install_key: installKey,
          active: true,
          user_id: user.id,
        });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['installations', user?.id] });
      form.reset();
      toast({
        title: "Installation created",
        description: "Your new installation has been created successfully.",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to create installation. Please try again.",
        variant: "destructive",
      });
      console.error('Error creating installation:', error);
    },
  });

  const deactivateInstallation = useMutation({
    mutationFn: async (id: string) => {
      if (!user?.id) throw new Error("User not authenticated");
      
      const { error } = await supabase
        .from('installations')
        .update({ active: false })
        .eq('id', id)
        .eq('user_id', user.id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['installations', user?.id] });
      toast({
        title: "Installation deactivated",
        description: "The installation has been deactivated successfully.",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to deactivate installation. Please try again.",
        variant: "destructive",
      });
      console.error('Error deactivating installation:', error);
    },
  });

  const onSubmit = (values: z.infer<typeof formSchema>) => {
    createInstallation.mutate(values.domain);
  };

  const handleViewScript = (installKey: string, domain: string) => {
    console.log("Opening modal with key:", installKey, "domain:", domain);
    setSelectedInstallation({ key: installKey, domain });
  };

  const handleConfigureVoice = (installKey: string, domain: string) => {
    setVoiceConfigInstallation({ key: installKey, domain });
  };

  if (!user) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Manage Installations</CardTitle>
          <CardDescription>
            Please sign in to manage your installations.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Manage Installations</CardTitle>
        <CardDescription>
          Create and manage your widget installations
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 mb-6">
            <FormField
              control={form.control}
              name="domain"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Domain</FormLabel>
                  <FormControl>
                    <Input placeholder="example.com" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button 
              type="submit" 
              disabled={createInstallation.isPending}
            >
              Create Installation
            </Button>
          </form>
        </Form>

        {isLoading ? (
          <div>Loading installations...</div>
        ) : installations && installations.length > 0 ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Domain</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Installation Key</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {installations.map((installation) => (
                <TableRow key={installation.id}>
                  <TableCell>{installation.domain}</TableCell>
                  <TableCell>
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      installation.active 
                        ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' 
                        : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400'
                    }`}>
                      {installation.active ? 'Active' : 'Inactive'}
                    </span>
                  </TableCell>
                  <TableCell className="font-mono text-sm">
                    {installation.install_key}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleViewScript(installation.install_key, installation.domain)}
                      >
                        View Script
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleConfigureVoice(installation.install_key, installation.domain)}
                      >
                        <Settings2 className="mr-2 h-4 w-4" />
                        Voice Assistant
                      </Button>
                      {installation.active && (
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => deactivateInstallation.mutate(installation.id)}
                          disabled={deactivateInstallation.isPending}
                        >
                          Deactivate
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <div className="text-center py-6 text-muted-foreground">
            No installations found. Create your first installation above.
          </div>
        )}

        {selectedInstallation && (
          <InstallationKeyModal 
            isOpen={!!selectedInstallation}
            onClose={() => setSelectedInstallation(null)}
            installationKey={selectedInstallation.key}
            domain={selectedInstallation.domain}
          />
        )}

        <InstallationVoiceConfigModal
          isOpen={!!voiceConfigInstallation}
          onClose={() => setVoiceConfigInstallation(null)}
          installationKey={voiceConfigInstallation?.key}
          domain={voiceConfigInstallation?.domain || ''}
        />
      </CardContent>
    </Card>
  );
}
